import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  removeWatchlistLoading,
  updateWatchlist,
} from "../redux/Watchlist/watchlist.slice";
import { mutate } from "swr";
import { AppNotificationType } from "../constants/appNotification";
import { IAppNotification } from "../types/app-notification";
import { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";

interface IUseHook {
  watchlistController: IWatchlistController;
  appNotification: IAppNotification;
  analyticsController: IAnalyticsController;
  trackWatchlistAdd: (context?: Record<string, any>) => void;
}

export type IUseWatchlist = ReturnType<typeof useWatchlist>;

export const useWatchlist = ({
  watchlistController,
  appNotification,
  analyticsController,
  trackWatchlistAdd,
}: IUseHook) => {
  const dispatch = useAppDispatch();

  const { userClinic, watchlist, loadingList, user, clinicFloorList } =
    useAppSelector((state) => ({
      userClinic: state.clinic.userClinic,
      clinicFloorList: state.clinic.clinicFloorList,
      watchlist: state.watchlist.watchlist,
      watchlistState: state.watchlist.watchlistState,
      loadingList: state.watchlist.loadingList,
      user: state.auth.user,
    }));

  const isInWatchlist = useCallback(
    (residentId: number) => {
      return !!watchlist.find((item) => item.resident.id === residentId);
    },
    [watchlist]
  );
  const revalidateMatcher = (key: string | object) =>
    JSON.stringify(key) ===
    JSON.stringify({
      clinicId: userClinic?.id!,
      apiName: "watchlist-rr",
    });

  const addToWatchlist = async (residentId: number) => {
    // Prevent multiple simultaneous operations on the same patient
    if (loadingList.includes(residentId)) {
      return { success: false };
    }

    dispatch(
      updateWatchlist({
        watchlistState: "loading",
        addLoadingItem: residentId,
      })
    );

    const { success, data, error } = await watchlistController.putWatchlist({
      clinicId: userClinic?.id!,
      residentId,
      watchlist: watchlist,
      floorIds: clinicFloorList.map((floor) => floor.floor_id) || [],
    });

    if (success && data) {
      analyticsController.logEvent("Add patient to watchlist", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });

      // Track watchlist add for engagement
      trackWatchlistAdd({
        residentId,
        clinicId: userClinic?.id,
      });

      dispatch(
        removeWatchlistLoading({
          watchlist: data.data,
          watchlistState: "pending",
          removeLoadingItem: residentId,
        })
      );
      await mutate(revalidateMatcher, undefined);

      return { success, data };
    }
    if (error) {
      dispatch(
        removeWatchlistLoading({
          watchlistState: "pending",
          removeLoadingItem: residentId,
        })
      );
      appNotification({
        nTitle: error.message,
        nType: AppNotificationType.ERROR,
      });
    }
    return { success: false };
  };

  const addToMultipleIdWatchlist = async (residentIds: number[]) => {
    const { success, data } = await watchlistController.putMultipleIdsWatchlist(
      {
        clinicId: userClinic?.id!,
        residentIds,
        watchlist: watchlist,
        floorIds: clinicFloorList.map((floor) => floor.floor_id) || [],
      }
    );

    if (success && data) {
      dispatch(
        removeWatchlistLoading({
          watchlist: data.data,
        })
      );

      return { success, data };
    }
    return { success: false };
  };

  const onRemoveClick = async (patientId: number) => {
    // Prevent multiple simultaneous operations on the same patient
    if (loadingList.includes(patientId)) {
      return { success: false };
    }

    dispatch(
      updateWatchlist({
        watchlistState: "loading",
        addLoadingItem: patientId,
      })
    );

    const deleteFilter = watchlist.filter((item) => {
      return item.resident.id !== patientId;
    });

    const { success, error } = await watchlistController.reorderWatchlist({
      clinicId: userClinic?.id!,
      watchlist: deleteFilter,
    });

    if (success) {
      await mutate(revalidateMatcher, undefined);

      dispatch(
        updateWatchlist({
          watchlist: deleteFilter,
        })
      );
      dispatch(
        removeWatchlistLoading({
          watchlistState: "pending",
          removeLoadingItem: patientId,
        })
      );

      return { success: true };
    }

    if (error) {
      dispatch(
        removeWatchlistLoading({
          watchlistState: "pending",
          removeLoadingItem: patientId,
        })
      );
      appNotification({
        nTitle: error.message || "Failed to remove from watchlist",
        nType: AppNotificationType.ERROR,
      });
    }

    return { success: false };
  };

  return {
    isInWatchlist,
    addToWatchlist,
    addToMultipleIdWatchlist,
    isAddingList: loadingList,
    removeFromWatchlist: onRemoveClick,
  };
};
